import { html, View } from 'rune-ts';
import { MShopUtilF } from '../../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import {
  makeEmptyScreenNavigate,
  makeSignUpSellerNavigate,
  makeSubScreenNavigate,
  makeUtilScreenNavigate,
  PostMessageNavigate,
} from '../../../../shared/app/navigate';
import { isApp } from '../../../../shared/app/util';
import { loading } from '../../../../shared/client/global';
import { dataStr } from '../../../../shared/util/dataStr';
import c from './Footer.module.scss';
import { Dropdown, DropdownChangeEvent, type DropdownOption } from '../Dropdown/Dropdown';
import { country_icon_urls } from '../../atoms/Icon';
import { changeUrlLang } from '../../../../shared/util';
import { font, type Lang, staticFont } from '../../../../shared/typography';
import { indexBy } from '@fxts/core';
import { SnsListView } from '@services/MarppleShop/renderApp/components/cells/Footer/views/SnsList/SnsList.ts';
import { CustomerSupportView } from './views/CustomerSupport/CustomerSupportView';
import { BusinessInformationView } from './views/BusinessInformation/BusinessInformationView';

export interface FooterData {
  out_links: {
    marpple_corporation: string;
    creator: string;
    plus: string;
    popup_store: string;
    offline_popup_store: string;
    online_popup_store: string;
    brand_commerce: string;
    marppleshop_x_youtube: string;
    handmade_creator: string;
    commerce_creator: string;
    goods_partner: string;

    customer_center: string;
    report_center: string;
    terms: string;
    privacy: string;
    specific_commerce_laws: string;
  };
  sns: {
    youtube?: string;
    instagram?: string;
    twitter?: string;
    naver_blog?: string;
    tiktok?: string;
  };
  lang: Lang;
  is_mobile: boolean;
}
export interface FooterState {
  is_info_btn_opened: boolean;
  lang_options: DropdownOption<Lang>[];
}

export enum FooterOutLink {
  // 마플코퍼레이션
  MCO = 'MCO',

  // 마플샵 크리에이터
  CREATOR = 'CREATOR',

  // 마플샵 플러스
  PLUS = 'PLUS',

  // 온라인 팝업스토어
  ONLINE_POPUP_STORE = 'ONLINE_POPUP_STORE',

  // 오프라인 팝업스토어
  OFFLINE_POPUP_STORE = 'OFFLINE_POPUP_STORE',

  // 핸드메이드 크리에이터
  HANDMADE_CREATOR = 'HANDMADE_CREATOR',

  // 커머스 크리에이터
  COMMERCE_CREATOR = 'COMMERCE_CREATOR',

  // 굿즈 파트너 크리에이터
  GOODS_PARTNER = 'GOODS_PARTNER',

  // 브랜드 커머스
  BRAND_COMMERCE = 'BRAND_COMMERCE',

  // 유튜브 쇼핑
  YOUTUBE_SHOPPING = 'YOUTUBE_SHOPPING',

  // 고객센터
  CUSTOMER_CENTER = 'CUSTOMER_CENTER',

  // 신고센터
  REPORT_CENTER = 'REPORT_CENTER',

  // 이용약관
  TERMS_OF_USE = 'TERMS_OF_USE',

  // 개인정보처리방침
  PRIVACY_POLICY = 'PRIVACY_POLICY',

  // 특정상거래법
  SPECIFIED_COMMERCIAL_LAW = 'SPECIFIED_COMMERCIAL_LAW',
}

const makeLangArray = (
  out_links: FooterData['out_links'],
): {
  id: FooterOutLink;
  name: string;
  link: string;
  post_message?: PostMessageNavigate;
}[] => {
  const links = [
    {
      id: FooterOutLink.MCO,
      name: ET('mps2::footer::마플코퍼레이션'),
      link: out_links.marpple_corporation,
    },
    {
      id: FooterOutLink.CREATOR,
      name: ET('mps2::footer::마플샵 크리에이터'),
      link: out_links.creator,
      post_message: makeSignUpSellerNavigate(out_links.creator),
    },
    {
      id: FooterOutLink.PLUS,
      name: ET('mps2::footer::마플샵 플러스'),
      link: out_links.plus,
      post_message: makeUtilScreenNavigate(out_links.plus),
    },
    {
      id: FooterOutLink.ONLINE_POPUP_STORE,
      name: ET('mps2::footer::온라인 팝업스토어'),
      link: out_links.online_popup_store,
      post_message: makeEmptyScreenNavigate(out_links.online_popup_store),
      predicate: () => true,
    },
    {
      id: FooterOutLink.OFFLINE_POPUP_STORE,
      name: ET('mps2::footer::오프라인 팝업스토어'),
      link: out_links.offline_popup_store,
      post_message: makeEmptyScreenNavigate(out_links.offline_popup_store),
    },
    {
      id: FooterOutLink.BRAND_COMMERCE,
      name: ET('mps2::footer::브랜드 커머스'),
      link: out_links.brand_commerce,
      post_message: makeEmptyScreenNavigate(out_links.brand_commerce),
    },
    {
      id: FooterOutLink.YOUTUBE_SHOPPING,
      name: ET('mps2::footer::유튜브 쇼핑'),
      link: out_links.marppleshop_x_youtube,
      post_message: makeEmptyScreenNavigate(out_links.marppleshop_x_youtube),
    },
    {
      id: FooterOutLink.HANDMADE_CREATOR,
      name: ET('mps2::footer::핸드메이드 크리에이터'),
      link: out_links.handmade_creator,
      post_message: makeEmptyScreenNavigate(out_links.handmade_creator),
    },
    {
      id: FooterOutLink.COMMERCE_CREATOR,
      name: ET('mps2::footer::커머스 크리에이터'),
      link: out_links.commerce_creator,
      post_message: makeEmptyScreenNavigate(out_links.commerce_creator),
    },
    {
      id: FooterOutLink.GOODS_PARTNER,
      name: ET('mps2::footer::굿즈 파트너스'),
      link: out_links.goods_partner,
      post_message: makeEmptyScreenNavigate(out_links.goods_partner),
    },
    {
      id: FooterOutLink.CUSTOMER_CENTER,
      name: ET('mps2::footer::고객센터'),
      link: out_links.customer_center,
      post_message: makeSubScreenNavigate(out_links.customer_center),
    },
    {
      id: FooterOutLink.REPORT_CENTER,
      name: ET('mps2::footer::신고센터'),
      link: out_links.report_center,
      post_message: makeSubScreenNavigate(out_links.report_center),
    },
    {
      id: FooterOutLink.TERMS_OF_USE,
      name: ET('mps2::footer::이용약관'),
      link: out_links.terms,
      post_message: makeSubScreenNavigate(out_links.terms),
    },
    {
      id: FooterOutLink.PRIVACY_POLICY,
      name: ET('mps2::footer::개인정보처리방침'),
      link: out_links.privacy,
      post_message: makeSubScreenNavigate(out_links.privacy),
    },
    {
      id: FooterOutLink.SPECIFIED_COMMERCIAL_LAW,
      name: '特定商取引法',
      link: out_links.specific_commerce_laws,
      post_message: makeSubScreenNavigate(out_links.specific_commerce_laws),
    },
  ];

  return links;
};

class FooterOutLinksHelper {
  private readonly hash: Record<
    FooterOutLink,
    {
      id: FooterOutLink;
      name: string;
      link: string;
      post_message?: PostMessageNavigate;
    }
  >;

  constructor(out_links: FooterData['out_links']) {
    const links = makeLangArray(out_links);
    this.hash = indexBy(({ id }) => id, links);
  }

  getQuickLinks() {
    return [
      this.hash.CREATOR,
      this.hash.PLUS,
      this.hash.ONLINE_POPUP_STORE,
      this.hash.OFFLINE_POPUP_STORE,
      this.hash.BRAND_COMMERCE,
      this.hash.YOUTUBE_SHOPPING,
      this.hash.HANDMADE_CREATOR,
      this.hash.COMMERCE_CREATOR,
      this.hash.GOODS_PARTNER,
    ];
  }

  getUsageGuideLinks() {
    return [this.hash.MCO, this.hash.TERMS_OF_USE];
  }

  getUsageGuideLinksBold() {
    return [this.hash.PRIVACY_POLICY];
  }
}

const makeLangOptionInner = (lang: Lang, name: string) => {
  return html`
    <span class="${c.dropdown_option}">
      <img class="${c.lang_icon}" src="${country_icon_urls[lang]}" />
      <span class="${font('14_medium_px')}">${name}</span>
    </span>
  `;
};

class FooterOutLinksSubView extends View<FooterData> {
  private footerOutLinksHelper = new FooterOutLinksHelper(this.data.out_links);

  protected override template() {
    return html`
      <div class="${c.outlink_wrap}">
        ${this.footerOutLinksHelper.getUsageGuideLinks().map((link) => {
          return html`<a
            href="${link.link}"
            class="${c.outlink} ${font('14_medium_px')}"
            ${link.post_message ? html`data-post-message="${dataStr(link.post_message)}"` : ''}
            target="_blank"
          >
            ${link.name}
          </a>`;
        })}
        ${this.footerOutLinksHelper.getUsageGuideLinksBold().map((link) => {
          return html`<a
            href="${link.link}"
            class="${c.outlink} ${font('14_bold_px')}"
            ${link.post_message ? html`data-post-message="${dataStr(link.post_message)}"` : ''}
            target="_blank"
          >
            ${link.name}
          </a>`;
        })}
      </div>
    `;
  }
}

class FooterLangSelectorSubView extends View<FooterData> {
  state: FooterState = {
    is_info_btn_opened: false,
    lang_options: [
      {
        key: 'kr',
        value: 'kr',
        name: makeLangOptionInner('kr', '한국어'),
      },
      { key: 'en', value: 'en', name: makeLangOptionInner('en', 'English') },
      { key: 'jp', value: 'jp', name: makeLangOptionInner('jp', '日本語') },
    ],
  };

  override template() {
    return html`
      <div class="${c.lang_wrap}">
        ${new Dropdown(
          { selected_option_key: this.data.lang, options: this.state.lang_options },
          {
            horizontal: 'right',
            vertical: 'top',
            klass: c.lang_dropdown,
            button_klass: c.lang_dropdown_button,
            has_arrow: true,
            text_align: 'left',
            use_px_system: true,
          },
        )}
      </div>
    `;
  }
}

export class FooterView extends View<FooterData> {
  private footerOutLinksSubView = new FooterOutLinksSubView(this.data);
  private businessInformationView = new BusinessInformationView(this.data);
  private customerSupportView = new CustomerSupportView(this.data);
  private snsListView = new SnsListView(this.data);
  private langSelectorSubView = new FooterLangSelectorSubView(this.data);

  override template() {
    if (this.data.is_mobile) {
      return html`
        <footer class="${c.footer}">
          <div class="${c.footer_content}">
            ${this.footerOutLinksSubView}
            <p class="${c.notice} ${font('12_medium_px')}">${ET('mps2::footer::notice')}</p>
            <div class="${c.business_information}">${this.businessInformationView}</div>
            <div class="${c.customer_support}">${this.customerSupportView}</div>
            <div class="${c.sns_container}">${this.snsListView}</div>
          </div>
          <div class="${c.section2}">
            ${this.langSelectorSubView}
            <span class="${c.mco} ${staticFont('unica_12_regular_px')}">© 2025 Marpple Corporation.</span>
          </div>
        </footer>
      `;
    } else {
      return html`
        <footer class="${c.footer}">
          <div class="${c.footer_content}">
            <div class="${c.layout_wrap}">
              <div class="${c.left}">
                ${this.footerOutLinksSubView}
                <p class="${c.notice} ${font('12_medium_px')}">${ET('mps2::footer::notice')}</p>
                <div class="${c.business_information}">${this.businessInformationView}</div>
                <span class="${c.mco} ${staticFont('unica_12_regular_px')}">© 2025 Marpple Corporation.</span
                >${this.langSelectorSubView}
              </div>
              <div class="${c.right}">
                <div class="${c.customer_support}">${this.customerSupportView}</div>
                <div class="${c.sns_container}">${this.snsListView}</div>
              </div>
            </div>
          </div>
        </footer>
      `;
    }
  }

  override onRender() {
    this.delegate(DropdownChangeEvent<Lang>, Dropdown, (e) => {
      if (!e.detail.option) return;

      if (isApp()) {
        loading();
        MShopUtilF.postMessage({ locale: e.detail.option.value });
        return;
      }

      location.href = changeUrlLang(location.href, e.detail.option.value);
    });
  }
}
